import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { CartState } from '@plys/src/application/states/cart.state';
import { GiftCardState } from '@plys/src/application/states/gift-card.state';
import { SubscriptionState } from '@plys/src/application/states/subscription.state';
import { isRecurringSubscription } from '@plys/src/models/subscription';
import { CartPreviewComponent } from '@plys/src/ui/cart-preview/cart-preview.component';
import { SubscriptionCartPreviewComponent } from '@plys/src/ui/subscription-cart-preview/subscription-cart-preview.component';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';
import { LocaleCurrencyPipe } from '../locale-currency.pipe';

/** Which state feeds the summary: the regular cart, or the subscription-only checkout. */
export type MobileCartFooterVariant = 'cart' | 'subscription';

@Component({
  selector: 'plys-mobile-cart-footer',
  standalone: true,
  imports: [LocaleCurrencyPipe, CommonModule, TranslocoModule, CartPreviewComponent, SubscriptionCartPreviewComponent],
  templateUrl: './mobile-cart-footer.component.html',
  styleUrls: ['./mobile-cart-footer.component.scss'],
})
export class MobileCartFooterComponent implements OnInit, OnDestroy {
  expanded = false;

  /**
   * The subscription flow never fills CartState's tickets/items, so it needs its own source for
   * "is there anything to show" and for the total — otherwise the footer stays hidden and mobile
   * users lose the back/continue buttons entirely.
   */
  @Input() set variant(value: MobileCartFooterVariant) {
    this._variant.next(value ?? 'cart');
  }

  private readonly _variant = new BehaviorSubject<MobileCartFooterVariant>('cart');

  /**
   * Payment step: break the first invoice out in the expanded panel — see
   * {@link SubscriptionCartPreviewComponent.firstTermCharge}, which this forwards — and name the
   * footer's total after it, so the breakdown and the total agree.
   */
  @Input() set firstTermCharge(value: boolean) {
    this._firstTermCharge.next(!!value);
  }

  private readonly _firstTermCharge = new BehaviorSubject(false);

  readonly firstTermCharge$ = this._firstTermCharge.asObservable();

  readonly isSubscription$ = this._variant.pipe(
    map((variant) => variant === 'subscription'),
    distinctUntilChanged()
  );

  private readonly cartHasContent$ = combineLatest([this.cartState.tickets$, this.cartState.items$]).pipe(
    map(([tickets, items]) => tickets.length > 0 || items.length > 0)
  );

  private readonly subscriptionHasContent$ = combineLatest([
    this.subscriptionState.selectedProduct$,
    this.giftCardState.hasSelection$,
  ]).pipe(map(([product, hasGiftCards]) => !!product || hasGiftCards));

  readonly hasCart$ = this._variant.pipe(
    switchMap((variant) => (variant === 'subscription' ? this.subscriptionHasContent$ : this.cartHasContent$)),
    distinctUntilChanged()
  );

  /**
   * The cart total in every flow, recurring subscriptions included: what the order comes to is the
   * figure the buyer is deciding on, and a per-month rate in the footer contradicted it — the plan's
   * per-period price is on its own row in the panel above, where it reads as a rate.
   */
  readonly total$ = this.cartState.total$;

  /** Whether this footer is summarising a recurring subscription — the only case the label turns on. */
  private readonly recurringSubscription$ = combineLatest([
    this.isSubscription$,
    this.subscriptionState.selectedProduct$,
  ]).pipe(map(([isSubscription, product]) => isSubscription && !!product && isRecurringSubscription(product.type)));

  /** Names what the payment-step total is the total of, matching the panel above it. */
  readonly totalLabelKey$ = combineLatest([this.recurringSubscription$, this._firstTermCharge]).pipe(
    map(([recurring, firstTerm]) =>
      recurring && firstTerm ? 'subscriptions.summary.total_first_invoice' : 'global.total'
    )
  );

  @HostBinding('class.keyboard-open')
  keyboardOpen = false;

  constructor(
    private cartState: CartState,
    private subscriptionState: SubscriptionState,
    private giftCardState: GiftCardState,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    window.visualViewport?.addEventListener('resize', this.onViewportResize);
  }

  ngOnDestroy(): void {
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
  }

  // Keyboard detection via Visual Viewport API: on iOS the visible viewport shrinks
  // when the keyboard opens, even when the focused input keeps focus after dismissal.
  private readonly onViewportResize = (): void => {
    const isOpen = (window.visualViewport?.height ?? window.innerHeight) < window.innerHeight * 0.75;
    this.ngZone.run(() => {
      this.keyboardOpen = isOpen;
    });
  };

  toggle(): void {
    this.expanded = !this.expanded;
  }
}
